import type { Paginated } from '../types/pagination'
import type {
  GenerateReportRequest,
  GenerateReportResponse,
  StatusReport,
  StatusReportListItem,
} from '../types/statusReport'
import { api } from './fetch'

class StatusReportService {
  /**
   * Get list of status reports for the current user
   * Returns reports created by the user or where the user is a recipient
   */
  async list(params?: { page?: number; page_size?: number }): Promise<
    Paginated<StatusReportListItem>
  > {
    const usp = new URLSearchParams()
    if (params?.page) usp.append('page', String(params.page))
    if (params?.page_size) usp.append('page_size', String(params.page_size))
    const qs = usp.toString()
    const response = await api.get<
      Paginated<StatusReportListItem> | StatusReportListItem[]
    >(`/tag/status-reports/${qs ? `?${qs}` : ''}`)
    const data = response.data as unknown
    if (Array.isArray(data)) {
      // Fallback: wrap array into paginated shape, order by -created_date
      let items = data as StatusReportListItem[]
      items = [...items].sort(
        (a, b) =>
          new Date(b.created_date).getTime() -
          new Date(a.created_date).getTime(),
      )
      const total = items.length
      const currentPage = Math.max(1, params?.page ?? 1)
      const size = Math.max(1, Math.min(params?.page_size ?? total, total))
      const start = (currentPage - 1) * size
      const paged = items.slice(start, start + size)
      const pages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1
      return { items: paged, count: total, page: currentPage, pages }
    }
    return data as Paginated<StatusReportListItem>
  }

  /**
   * Get detailed status report by ID
   * Returns full report with snapshot data
   */
  async getById(id: number): Promise<StatusReport> {
    const response = await api.get(`/tag/status-reports/${id}/`)
    return response.data
  }

  /**
   * Get status reports created by the current user
   */
  async listCreated(params?: { page?: number; page_size?: number }): Promise<
    Paginated<StatusReportListItem>
  > {
    const usp = new URLSearchParams()
    if (params?.page) usp.append('page', String(params.page))
    if (params?.page_size) usp.append('page_size', String(params.page_size))
    const qs = usp.toString()
    const response = await api.get<
      Paginated<StatusReportListItem> | StatusReportListItem[]
    >(`/tag/status-reports/created/${qs ? `?${qs}` : ''}`)
    const data = response.data as unknown
    if (Array.isArray(data)) {
      let items = data as StatusReportListItem[]
      items = [...items].sort(
        (a, b) =>
          new Date(b.created_date).getTime() -
          new Date(a.created_date).getTime(),
      )
      const total = items.length
      const currentPage = Math.max(1, params?.page ?? 1)
      const size = Math.max(1, Math.min(params?.page_size ?? total, total))
      const start = (currentPage - 1) * size
      const paged = items.slice(start, start + size)
      const pages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1
      return { items: paged, count: total, page: currentPage, pages }
    }
    return data as Paginated<StatusReportListItem>
  }

  /**
   * Generate a new tag report and create a status report
   * Returns the generated report data and status report ID
   * The backend creates a StatusReport and returns the ID in the response
   */
  async generateReport(
    request: GenerateReportRequest,
  ): Promise<{ report: GenerateReportResponse; statusReportId: number }> {
    const response = await api.post('/tag/report/generate/', request)
    const report = response.data

    // The backend now returns the status report ID in the response
    const statusReportId = report.status_report_id

    if (!statusReportId) {
      throw new Error('Status report ID not found in response')
    }

    return {
      report,
      statusReportId,
    }
  }
  /**
   * Update a status report (notes, status, recipients)
   * Only the creator can update
   */
  async update(
    id: number,
    data: {
      recipient_user_ids?: number[]
      notes?: string
      status?: string
    },
  ): Promise<StatusReport> {
    const response = await api.patch(`/tag/status-reports/${id}/`, data)
    return response.data
  }

  /**
   * Delete a status report
   * Only the creator can delete
   */
  async delete(id: number): Promise<void> {
    await api.delete(`/tag/status-reports/${id}/`)
  }

  /**
   * Download status report as PDF
   */
  async downloadPdf(id: number): Promise<void> {
    const response = await api.get(`/tag/status-reports/${id}/pdf/`, {
      responseType: 'blob',
    })
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `status_report_${id}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  /**
   * Download status report as CSV
   */
  async downloadCsv(id: number): Promise<void> {
    const response = await api.get(`/tag/status-reports/${id}/csv/`, {
      responseType: 'blob',
    })
    const blob = new Blob([response.data], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `status_report_${id}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  /**
   * Get completed tag IDs for a project (across all its dispatches)
   */
  async getProjectCompletedTagIds(
    projectId: number,
  ): Promise<{ tag_ids: number[]; count: number }> {
    const response = await api.get(
      `/tag/report/project/${projectId}/completed-tag-ids/`,
    )
    return response.data
  }
}

export const statusReportService = new StatusReportService()
